import { useState } from 'react'
import { FolderPlus, Loader2, Plus, Check } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useVisualCollections, useCollectionMembership } from '../../hooks/useVisualCollections'
import type { VisualCollection } from '../../lib/api'

interface AddToCollectionModalProps {
  isOpen: boolean
  onClose: () => void
  taskId: string
  /** Optional: ids of collections the task already belongs to (for visual indicator). */
  memberCollectionIds?: string[]
}

export function AddToCollectionModal({
  isOpen,
  onClose,
  taskId,
  memberCollectionIds = [],
}: AddToCollectionModalProps) {
  const { collections, isLoading, createCollection, isCreating } = useVisualCollections()
  const { addToCollection, removeFromCollection, isAdding, isRemoving } = useCollectionMembership()

  const [newName, setNewName] = useState('')
  const [showNewForm, setShowNewForm] = useState(false)
  const [pendingCollectionId, setPendingCollectionId] = useState<string | null>(null)

  const isMember = (collection: VisualCollection) =>
    memberCollectionIds.includes(collection.id)

  const handleToggle = async (collection: VisualCollection) => {
    setPendingCollectionId(collection.id)
    try {
      if (isMember(collection)) {
        await removeFromCollection({ collectionId: collection.id, taskId })
      } else {
        await addToCollection({ collectionId: collection.id, taskId })
      }
    } finally {
      setPendingCollectionId(null)
    }
  }

  const handleCreate = async () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    const created = await createCollection({ name: trimmed })
    setNewName('')
    setShowNewForm(false)
    await addToCollection({ collectionId: created.id, taskId })
  }

  const isBusy = isAdding || isRemoving

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Agregar a coleccion" size="sm">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="md" />
          </div>
        ) : collections.length === 0 && !showNewForm ? (
          <div className="text-center py-6 text-slate-500">
            <FolderPlus className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm mb-4">No hay colecciones todavia</p>
            <button
              type="button"
              onClick={() => setShowNewForm(true)}
              className="btn btn-primary text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Crear primera coleccion
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {collections.map((col) => {
              const member = isMember(col)
              const loading = pendingCollectionId === col.id && isBusy
              return (
                <li key={col.id}>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleToggle(col)}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors text-left disabled:opacity-60"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{col.name}</p>
                      {col.description && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{col.description}</p>
                      )}
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      ) : member ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-700 font-medium">
                          <Check className="w-4 h-4" />
                          Agregada
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 group-hover:text-slate-600">
                          Agregar
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {/* New collection inline form */}
        {showNewForm ? (
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <input
              autoFocus
              type="text"
              className="input flex-1 text-sm"
              placeholder="Nombre de la coleccion"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
                if (e.key === 'Escape') {
                  setShowNewForm(false)
                  setNewName('')
                }
              }}
            />
            <button
              type="button"
              disabled={!newName.trim() || isCreating}
              onClick={handleCreate}
              className="btn btn-primary text-sm px-3"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowNewForm(false)
                setNewName('')
              }}
              className="btn btn-secondary text-sm px-3"
            >
              Cancelar
            </button>
          </div>
        ) : (
          collections.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowNewForm(true)}
                className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Nueva coleccion
              </button>
            </div>
          )
        )}
      </div>
    </Modal>
  )
}
